import { Address, erc20Abi, parseAbi, zeroAddress } from 'viem';
import { QueryOptions } from 'utils/useQueryUtils';
import { useConnection, usePublicClient } from 'wagmi';
import { queryKeys } from 'structure/pv2/vault/configs/queryKeys';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DynaVault } from '../../../../api/DynaVaultsAPI';
import { appConfig } from 'utils/app.config';
import { DynaVaultABI } from 'utils/constants/abi/DynaVaultABI';
import { getStoredTokens } from 'structure/portfolio/utils/portfolio';
import { MulticallReturnType } from 'utils/types';
import { formatUnits } from 'utils/units';
import { getRegistryVaultType } from 'structure/pv2/dynavault/utils/vaultStatus';
import {
  ChainId,
  getVaultRegistryAddress,
  VaultRegistryABI,
} from 'sfi-vault-sdk';
import { getOracleDeployment } from 'sfi-oracle-sdk';
import { sumVaultTvlUsd } from 'structure/pv2/dynavault/utils/vaultTvl';

interface UseVaultApyListProps extends QueryOptions {
  chainId: number;
}

export const useVaultListBlockchain = (options: UseVaultApyListProps) => {
  const { query, chainId } = options;

  const account = useConnection();
  const tokens = getStoredTokens();
  const authToken = tokens[account.address || ''];

  const publicClient = usePublicClient({ chainId });

  const useQueryOptions = {
    queryKey: queryKeys.vaultListBlockchain({
      walletAddress: account.address,
      isAuth: !!authToken,
      chainId,
    }),
    ...query,
  };

  return useQuery<DynaVault[]>({
    ...useQueryOptions,
    placeholderData: [],
    queryFn: async () => {
      if (!publicClient) {
        throw new Error('Public client not available');
      }

      const registryAddress = getVaultRegistryAddress(chainId);
      if (!registryAddress) {
        throw new Error(`No vault registry found for chainId ${chainId}`);
      }

      // Fetch number of vaults
      const numberOfVaults = await publicClient.readContract({
        address: registryAddress,
        abi: VaultRegistryABI,
        functionName: 'nrOfVaults',
        authorizationList: undefined,
      });

      // Fetch vaults in batches
      const batchSize = 100;
      let dynaVaults: { vault: Address; vaultType: number; active: boolean }[] =
        [];

      for (let i = 0; i < Number(numberOfVaults); i += batchSize) {
        const size = Math.min(batchSize, Number(numberOfVaults) - i);
        const batch = await publicClient.readContract({
          address: registryAddress,
          abi: VaultRegistryABI,
          functionName: 'getVaults',
          args: [BigInt(i), BigInt(size)],
          authorizationList: undefined,
        });

        dynaVaults = [...dynaVaults, ...batch];
      }

      const vaultTypes =
        appConfig.isProduction === true ? [3, 4] : [1, 2, 3, 4]; // 1: DEV, 2: UAT, 3: BETA, 4: PRODUCTION

      // Filter active vaults
      const activeVaultsInProdOrBetaOrUatOrDev = dynaVaults.filter(
        (vault) => vault.active && vaultTypes.includes(vault.vaultType)
      );

      const vaultDataCalls = activeVaultsInProdOrBetaOrUatOrDev
        .map((vault) => [
          {
            address: vault.vault,
            abi: DynaVaultABI,
            functionName: 'symbol',
            authorizationList: undefined,
          },
          {
            address: vault.vault,
            abi: DynaVaultABI,
            functionName: 'name',
            authorizationList: undefined,
          },
          {
            address: vault.vault,
            abi: DynaVaultABI,
            functionName: 'manager',
            authorizationList: undefined,
          },
          {
            address: vault.vault,
            abi: DynaVaultABI,
            functionName: 'totalAssets',
            authorizationList: undefined,
          },
          {
            address: vault.vault,
            abi: erc20Abi,
            functionName: 'decimals',
            authorizationList: undefined,
          },
          {
            address: vault.vault,
            abi: DynaVaultABI,
            functionName: 'asset',
            authorizationList: undefined,
          },
        ])
        .flat();

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const vaultDataResponses = await (publicClient as any)
        .multicall({
          contracts: vaultDataCalls,
          authorizationList: undefined,
        })
        .catch((error: unknown) => {
          console.error('Multicall for vault data failed:', error);
          return [];
        });

      const methodNumber =
        vaultDataCalls.length / activeVaultsInProdOrBetaOrUatOrDev.length;

      const results: DynaVault[] = [];
      for (let i = 0; i < activeVaultsInProdOrBetaOrUatOrDev.length; i++) {
        const vaultInfo = activeVaultsInProdOrBetaOrUatOrDev[i];
        const baseIndex = i * methodNumber;
        if (
          !vaultInfo ||
          baseIndex + methodNumber > vaultDataResponses.length
        ) {
          console.warn(`Skipping index ${i}: insufficient data`);
          continue;
        }

        const [symbol, name, managementWallet, totalAssets, decimals, asset] =
          vaultDataResponses.slice(baseIndex, baseIndex + methodNumber) as [
            MulticallReturnType<string>,
            MulticallReturnType<string>,
            MulticallReturnType<string>,
            MulticallReturnType<bigint>,
            MulticallReturnType<number>,
            MulticallReturnType<string>,
          ];

        results.push({
          id: i + 1,
          chainId,
          name: name.result || '',
          symbol: symbol.result || '',
          decimals: decimals.result || 0,
          asset: asset.result || '0x0',
          depositToken: asset.result || '0x0',
          address: vaultInfo.vault,
          managementFees: 0,
          performanceFee: 0,
          ticker: '',
          factory: '',
          activation: '1999-12-31T23:00:00.000Z',
          managementWallet: managementWallet.result || '',
          governanceWallet: '',
          guardianWallet: '',
          functioningType: 'DynaVault', // ??
          vaultType: getRegistryVaultType(vaultInfo.vaultType),
          isClosed: false,
          sinceInceptionAPY: 0,
          twentyFourHourAPY: 0,
          weeklyAPY: 0,
          monthlyAPY: 0,
          yearlyAPY: 0,
          fees: [],
          apr: '0',
          type: 'YIELD',
          riskLevel: 'HIGH',
          description: '',
          factsheets: [],
          iconUrl: '',
          twentyFourHourPerformance: '0',
          weeklyPerformance: '0',
          monthlyPerformance: '0',
          yearlyPerformance: '0',
          sinceInceptionPerformance: '0',
          netPerformance: '0',
          lastReport: '0',
          lastComputed: '1999-12-31T23:00:00.000Z',
          totalSupply: (totalAssets.result ?? BigInt(0)).toString(),
          price: 0, // ?
          availableFunds: '0',
          deployedFunds: '0',
          resourceId: 1,
          createdAt: '1999-12-31T23:00:00.000Z',
          updatedAt: '1999-12-31T23:00:00.000Z',
          userWalletAddress: '',
          metadata: {
            contracts: {
              type: 'DynaVault',
              DynaVault: vaultInfo.vault,
              DynaRouter: '0x0',
              VaultZapper: '0x0',
              dynaLiquidity: '0x0',
              BalancerV2Zapper: '0x0',
              DynaVaultManager: '0x0',
            },
          },
        } as DynaVault);
      }

      return results;
    },
    enabled: !!publicClient && !!getVaultRegistryAddress(chainId),
  });
};

export interface VaultOnChainData {
  chainId: number;
  name: string;
  symbol: string;
  decimals: number;
  address: `0x${string}`;
  underlyingAsset: `0x${string}`;
  underlyingAssetSymbol: string;
  underlyingAssetDecimals: number;
  manager: `0x${string}`;
  totalAssets: bigint;
  referenceAsset: `0x${string}`;
  tokenValueInQuoteAsset: bigint;
  referenceAssetOracle: `0x${string}`;
  vaultPrice: string;
  tvlUsd: string;
  walletBalance: bigint;
  walletAssets: bigint;
  positionInUsd: number;
}

interface UseVaultListOnChainDataProps extends QueryOptions {
  vaultAddressList: `0x${string}`[];
  walletAddress: `0x${string}`;
  chainId: number;
}

export const useVaultListOnChainData = (
  params: UseVaultListOnChainDataProps
) => {
  const { vaultAddressList, chainId, walletAddress, query } = params;

  const publicClient = usePublicClient({ chainId });
  const oracleDeployment = getOracleDeployment(chainId);

  const qc = useQueryClient();

  const queryOptions = {
    ...query,
    queryKey: queryKeys.vaultOnChainDataList({
      chainId,
      addressList: vaultAddressList,
      walletAddress,
    }),
    enabled:
      !!publicClient &&
      !!oracleDeployment &&
      vaultAddressList.length > 0 &&
      query?.enabled,
  };

  return useQuery({
    ...queryOptions,
    queryFn: async () => {
      if (!publicClient || !oracleDeployment) {
        throw new Error(`No oracle deployment found for chainId ${chainId}`);
      }

      const quoteAsset = oracleDeployment.referenceAsset;
      const quoteAssetDecimals = await publicClient.readContract({
        address: quoteAsset,
        abi: erc20Abi,
        functionName: 'decimals',
        authorizationList: undefined,
      });

      const vaultDataCalls = vaultAddressList
        .map((address) => [
          {
            address: address,
            abi: DynaVaultABI,
            functionName: 'symbol',
            authorizationList: undefined,
          },
          {
            address: address,
            abi: DynaVaultABI,
            functionName: 'name',
            authorizationList: undefined,
          },
          {
            address: address,
            abi: DynaVaultABI,
            functionName: 'manager',
            authorizationList: undefined,
          },
          {
            address: address,
            abi: DynaVaultABI,
            functionName: 'totalAssets',
            authorizationList: undefined,
          },
          {
            address: address,
            abi: erc20Abi,
            functionName: 'decimals',
            authorizationList: undefined,
          },
          {
            address: address,
            abi: DynaVaultABI,
            functionName: 'referenceAsset',
            authorizationList: undefined,
          },
          {
            address: address,
            abi: DynaVaultABI,
            functionName: 'referenceAssetOracle',
            authorizationList: undefined,
          },
          {
            address: address,
            abi: DynaVaultABI,
            functionName: 'asset',
            authorizationList: undefined,
          },
          {
            address: address,
            abi: erc20Abi,
            functionName: 'balanceOf',
            args: [walletAddress || zeroAddress],
          },
        ])
        .flat();

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const vaultDataResponses = await publicClient.multicall({
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        contracts: vaultDataCalls,
        authorizationList: undefined,
      });

      const methodNumber = vaultDataCalls.length / vaultAddressList.length;

      const vaultMap = new Map<`0x${string}`, VaultOnChainData>();
      for (let i = 0; i < vaultAddressList.length; i++) {
        const vaultAddress = vaultAddressList[i];
        const baseIndex = i * methodNumber;
        if (
          !vaultAddress ||
          baseIndex + methodNumber > vaultDataResponses.length
        ) {
          console.warn(
            `Skipping index i:${i}/baseIndex:${baseIndex}/extendedMethodNumber:${methodNumber}: insufficient data`
          );
          continue;
        }

        const [
          symbol,
          name,
          managerAddress,
          totalAssets,
          decimals,
          referenceAsset,
          referenceAssetOracle,
          underlyingAsset,
          userBalance,
        ] = vaultDataResponses.slice(baseIndex, baseIndex + methodNumber) as [
          MulticallReturnType<string>,
          MulticallReturnType<string>,
          MulticallReturnType<`0x${string}`>,
          MulticallReturnType<bigint>,
          MulticallReturnType<number>,
          MulticallReturnType<`0x${string}`>,
          MulticallReturnType<`0x${string}`>,
          MulticallReturnType<`0x${string}`>,
          MulticallReturnType<bigint>,
        ];

        vaultMap.set(vaultAddress, {
          chainId,
          name: name.result || '',
          symbol: symbol.result || '',
          decimals: decimals.result || 0,
          address: vaultAddress,
          underlyingAsset: (underlyingAsset.result ?? '0x0') as `0x${string}`,
          underlyingAssetSymbol: '',
          underlyingAssetDecimals: decimals.result || 0,
          manager: (managerAddress.result ?? '0x0') as `0x${string}`,
          totalAssets: (totalAssets.result ?? BigInt(0)) as bigint,
          referenceAsset: (referenceAsset.result ?? '0x0') as `0x${string}`,
          referenceAssetOracle: (referenceAssetOracle.result ??
            '0x0') as `0x${string}`,
          tokenValueInQuoteAsset: BigInt(0),
          vaultPrice: '0',
          tvlUsd: '0',
          walletBalance: (userBalance.result ?? BigInt(0)) as bigint,
          walletAssets: BigInt(0),
          positionInUsd: 0,
        });
      }

      const vaultExtendedDataCalls = Array.from(vaultMap)
        .map(([address, vault]) => [
          {
            address: address,
            abi: DynaVaultABI,
            functionName: 'tokenValueInQuoteAsset',
            args: [vault.referenceAsset, vault.totalAssets, quoteAsset],
          },
          {
            address: vault.referenceAssetOracle,
            abi: parseAbi([
              'function getPrice(address base, address quote) view returns (uint256 price, uint256 oldestObservation)',
            ]),
            functionName: 'getPrice',
            args: [address, quoteAsset],
            authorizationList: undefined,
          },
          {
            address,
            abi: DynaVaultABI,
            functionName: 'convertToAssets',
            args: [vault.walletBalance],
            authorizationList: undefined,
          },
          {
            address: vault.underlyingAsset,
            abi: erc20Abi,
            functionName: 'symbol',
            authorizationList: undefined,
          },
          {
            address: vault.underlyingAsset,
            abi: erc20Abi,
            functionName: 'decimals',
            authorizationList: undefined,
          },
        ])
        .flat();

      const extendedMethodNumber =
        vaultExtendedDataCalls.length / vaultAddressList.length;

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const vaultExtendedData = await publicClient.multicall({
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        contracts: vaultExtendedDataCalls,
        authorizationList: undefined,
      });

      for (let i = 0; i < vaultAddressList.length; i++) {
        const vaultAddress = vaultAddressList[i];
        const baseIndex = i * extendedMethodNumber;
        if (
          !vaultAddress ||
          baseIndex + extendedMethodNumber > vaultExtendedData.length
        ) {
          console.warn(
            `Skipping index i:${i}/baseIndex:${baseIndex}/extendedMethodNumber:${extendedMethodNumber}: insufficient data`
          );
          continue;
        }

        const [
          tokenValueInQuoteAsset,
          vaultPrice,
          walletAssets,
          underlyingAssetSymbol,
          underlyingAssetDecimals,
        ] = vaultExtendedData.slice(
          baseIndex,
          baseIndex + extendedMethodNumber
        ) as [
          MulticallReturnType<bigint>,
          MulticallReturnType<bigint>,
          MulticallReturnType<bigint>,
          MulticallReturnType<string>,
          MulticallReturnType<number>,
        ];

        const vault = vaultMap.get(vaultAddress);
        if (!vault) continue;

        const vaultPriceResult = vaultPrice.result as
          | [bigint, bigint]
          | undefined;
        const vaultPriceInUsd = formatUnits(
          vaultPriceResult?.[0] ?? BigInt(0),
          quoteAssetDecimals
        );

        const positionUsd =
          parseFloat(formatUnits(vault.walletBalance, vault.decimals)) *
          Number(vaultPriceInUsd);

        vaultMap.set(vaultAddress, {
          ...vault,
          tokenValueInQuoteAsset: (tokenValueInQuoteAsset.result ??
            BigInt(0)) as bigint,
          tvlUsd: formatUnits(
            (tokenValueInQuoteAsset.result ?? BigInt(0)) as bigint,
            quoteAssetDecimals
          ),
          vaultPrice: vaultPriceInUsd,
          underlyingAssetSymbol: underlyingAssetSymbol.result ?? '',
          underlyingAssetDecimals:
            underlyingAssetDecimals.result ?? vault.decimals,
          walletAssets: (walletAssets.result ?? BigInt(0)) as bigint,
          positionInUsd: positionUsd,
        });
      }

      const map: Record<`0x${string}`, VaultOnChainData> =
        Object.fromEntries(vaultMap);
      qc.setQueryData<Record<`0x${string}`, VaultOnChainData>>(
        queryKeys.vaultOnChainDataByAddress({ chainId }),
        (prev) => ({ ...(prev ?? {}), ...map })
      );

      return map;
    },
  });
};

interface UseVaultListOnChainDataAcrossChainsProps extends QueryOptions {
  vaultList: DynaVault[];
  walletAddress: `0x${string}`;
}

export const useVaultListOnChainDataAcrossChains = (
  params: UseVaultListOnChainDataAcrossChainsProps
) => {
  const { vaultList, walletAddress, query } = params;
  const ethereumVaults = vaultList.filter(
    (vault) => vault.chainId === ChainId.ETHEREUM
  );
  const baseVaults = vaultList.filter(
    (vault) => vault.chainId === ChainId.BASE
  );
  const bnbVaults = vaultList.filter((vault) => vault.chainId === ChainId.BNB);
  const optimismVaults = vaultList.filter(
    (vault) => vault.chainId === ChainId.OPTIMISM
  );
  const arbitrumVaults = vaultList.filter(
    (vault) => vault.chainId === ChainId.ARBITRUM
  );
  const polygonVaults = vaultList.filter(
    (vault) => vault.chainId === ChainId.POLYGON
  );

  const ethereumQuery = useVaultListOnChainData({
    vaultAddressList: ethereumVaults.map((vault) => vault.address),
    walletAddress,
    chainId: ChainId.ETHEREUM,
    query,
  });
  const baseQuery = useVaultListOnChainData({
    vaultAddressList: baseVaults.map((vault) => vault.address),
    walletAddress,
    chainId: ChainId.BASE,
    query,
  });
  const bnbQuery = useVaultListOnChainData({
    vaultAddressList: bnbVaults.map((vault) => vault.address),
    walletAddress,
    chainId: ChainId.BNB,
    query,
  });
  const optimismQuery = useVaultListOnChainData({
    vaultAddressList: optimismVaults.map((vault) => vault.address),
    walletAddress,
    chainId: ChainId.OPTIMISM,
    query,
  });
  const arbitrumQuery = useVaultListOnChainData({
    vaultAddressList: arbitrumVaults.map((vault) => vault.address),
    walletAddress,
    chainId: ChainId.ARBITRUM,
    query,
  });
  const polygonQuery = useVaultListOnChainData({
    vaultAddressList: polygonVaults.map((vault) => vault.address),
    walletAddress,
    chainId: ChainId.POLYGON,
    query,
  });

  return {
    data: {
      ...(baseQuery.data ?? {}),
      ...(ethereumQuery.data ?? {}),
      ...(bnbQuery.data ?? {}),
      ...(optimismQuery.data ?? {}),
      ...(arbitrumQuery.data ?? {}),
      ...(polygonQuery.data ?? {}),
    },
    totalTvlUsd: sumVaultTvlUsd(
      Object.values(ethereumQuery.data ?? {}),
      Object.values(baseQuery.data ?? {}),
      Object.values(bnbQuery.data ?? {}),
      Object.values(optimismQuery.data ?? {}),
      Object.values(arbitrumQuery.data ?? {}),
      Object.values(polygonQuery.data ?? {})
    ),
    isLoading:
      (baseVaults.length > 0 && baseQuery.isLoading) ||
      (ethereumVaults.length > 0 && ethereumQuery.isLoading) ||
      (bnbVaults.length > 0 && bnbQuery.isLoading) ||
      (optimismVaults.length > 0 && optimismQuery.isLoading) ||
      (arbitrumVaults.length > 0 && arbitrumQuery.isLoading) ||
      (polygonVaults.length > 0 && polygonQuery.isLoading),
    isFetched:
      (baseVaults.length === 0 || baseQuery.isFetched) &&
      (ethereumVaults.length === 0 || ethereumQuery.isFetched) &&
      (bnbVaults.length === 0 || bnbQuery.isFetched) &&
      (optimismVaults.length === 0 || optimismQuery.isFetched) &&
      (arbitrumVaults.length === 0 || arbitrumQuery.isFetched) &&
      (polygonVaults.length === 0 || polygonQuery.isFetched),
    refetch: () =>
      Promise.all([
        ...(baseVaults.length > 0 ? [baseQuery.refetch()] : []),
        ...(ethereumVaults.length > 0 ? [ethereumQuery.refetch()] : []),
        ...(bnbVaults.length > 0 ? [bnbQuery.refetch()] : []),
        ...(optimismVaults.length > 0 ? [optimismQuery.refetch()] : []),
        ...(arbitrumVaults.length > 0 ? [arbitrumQuery.refetch()] : []),
        ...(polygonVaults.length > 0 ? [polygonQuery.refetch()] : []),
      ]),
  };
};

export function useVaultOnChainData(props: {
  vaultAddress: `0x${string}`;
  chainId: number;
  walletAddress: string;
}) {
  const { vaultAddress, chainId } = props;

  return useQuery({
    queryKey: queryKeys.vaultOnChainDataByAddress({ chainId }),
    enabled: true,
    queryFn: async () => ({}) as Record<`0x${string}`, VaultOnChainData>,
    staleTime: Infinity,
    select: (m) => m?.[vaultAddress],
  });
}
